import { useTranslation } from "react-i18next"
import React, { useMemo, useRef, useState } from "react"
import DataTable from "react-data-table-component"
import { PPDefaultSelector } from "../ui/PPDefaultSelector"
import { gql, useMutation } from "@apollo/client"
import { toast } from "react-toastify"
import PPExpandedRowUserAdministration from "./PPExpandedRowUserAdministration"
import PPUtils from "../../utils/PPUtils"
import "../../styles/pricetable.css"

const rights = [
    { label: "Admin", value: "admin" },
    { label: "User", value: "user" }
]

const DOM_TO_API = {
    f1: "username",
    f2: "password",
    f3: "name",
    f4: "email"
}

function AdminTextField({ id, field, defaultValue, inputRef, onChange }) {
    return (
        <input
            id={id}
            type="text"
            className="form-control pp-bw-guard"
            name={field}
            data-pp-field={field}
            defaultValue={defaultValue}
            ref={inputRef}
            autoComplete="off"
            data-bwignore="true"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            readOnly
            onFocus={(e) => {
                e.target.readOnly = false
            }}
            onChange={onChange}
        />
    )
}

const CREATE_USER_QUERY = gql`
    mutation ($user: UserInput!) {
        createUser(user: $user) {
            email
        }
    }
`
const UPDATE_USER_QUERY = gql`
    mutation ($user: UserInput!) {
        UpdateUser(user: $user) {
            email
        }
    }
`

export function PPUserAdministration(props) {
    const [t, i18n] = useTranslation()
    let auth = PPUtils.getLogin()
    let customer_activ = false
    let customers = []
    if (auth?.user?.rights == "admin" && auth.customerUsers?.length >= 1 && props?.customers?.length > 0) {
        customer_activ = true
        customers = props?.customers.reduce(function (previous, current) {
            return previous.concat({ value: current.id, label: current.name })
        }, [])
    }

    const [buttonCreateDisabled, setButtonCreateDisabled] = useState(true)

    const [languages] = useState(
        Object.keys(i18n.services.resourceStore.data).map((x) => ({
            value: x,
            label: t("app.langs." + x)
        }))
    )

    const [rightsRef, setRights] = useState(rights.find((e) => e.value == "user"))
    const usernameRef = useRef()
    const passwordRef = useRef()
    const nameRef = useRef()
    const emailRef = useRef()
    const [languageRef, setLanguage] = useState(languages.find((e) => e.value == "de"))

    const columns = useMemo(() => [
        {
            name: "ID",
            selector: (row) => row.id,
            width: "56px",
            grow: 0
        },
        {
            name: t("single.rights"),
            minWidth: "120px",
            grow: 1,
            cell: (row) => {
                return (
                    <PPDefaultSelector
                        name="rights"
                        data={rights}
                        defaultValue={rights.find((e) => e.value == row.rights)}
                        isClearable={false}
                    />
                )
            }
        },
        {
            name: t("single.username"),
            minWidth: "120px",
            grow: 1,
            cell: (row) => {
                return (
                    <AdminTextField field="f1" defaultValue={row.username} />
                )
            }
        },
        {
            name: t("single.password"),
            minWidth: "120px",
            grow: 1,
            cell: () => {
                return (
                    <AdminTextField field="f2" />
                )
            }
        },
        {
            name: t("single.name"),
            minWidth: "120px",
            grow: 1,
            cell: (row) => {
                return (
                    <AdminTextField field="f3" defaultValue={row.name} />
                )
            }
        },
        {
            name: "E-Mail",
            minWidth: "140px",
            grow: 1,
            cell: (row) => {
                return (
                    <AdminTextField field="f4" defaultValue={row.email} />
                )
            }
        },
        {
            name: t("single.language"),
            minWidth: "110px",
            grow: 1,
            cell: (row) => {
                return (
                    <PPDefaultSelector
                        name="configLanguage"
                        data={languages}
                        defaultValue={languages.find((e) => e.value == row.configLanguage)}
                        isClearable={false}
                    />
                )
            }
        },
        {
            name: t("single.hidden"),
            width: "88px",
            grow: 0,
            cell: (row) => {
                return (
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            id={"form-check-input-hidden-row-" + row.id}
                            className="form-check-input"
                            name="hidden"
                            defaultChecked={row.hidden}
                            onChange={(e) => {
                                row.hidden = e.target.checked
                            }}
                        />
                        <label className="form-check-label" htmlFor={"form-check-input-hidden-row-" + row.id}>
                            <span className="visually-hidden">{t("single.hidden")}</span>
                        </label>
                    </div>
                )
            }
        },
        {
            name: t("single.ruleEngineAccess"),
            width: "140px",
            grow: 0,
            cell: (row) => {
                return (
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            id={"form-check-input-reaccess-row-" + row.id}
                            className="form-check-input"
                            name="ruleEngineAccess"
                            defaultChecked={row.ruleEngineAccess}
                            onChange={(e) => {
                                row.ruleEngineAccess = e.target.checked
                            }}
                        />
                        <label
                            className="form-check-label"
                            htmlFor={"form-check-input-reaccess-row-" + row.id}>
                            <span className="visually-hidden">{t("single.ruleEngineAccess")}</span>
                        </label>
                    </div>
                )
            }
        },
        {
            name: "",
            width: "130px",
            grow: 0,
            cell: (row) => {
                return (
                    <button
                        className="btn btn-primary text-nowrap"
                        onClick={() => {
                            const rowEl = document.getElementById("row-" + row.id)
                            const isExpanded = rowEl?.nextElementSibling?.classList.contains("rdt_ExpanderRow")
                            if (!isExpanded) {
                                rowEl?.querySelector("div button")?.click()
                            }
                            setTimeout(function () {
                                submitUpdateUser(row)
                            }, 300)
                        }}
                        type="button">
                        {t("single.save")}
                    </button>
                )
            }
        }
    ], [t, languages])

    const [createUser] = useMutation(CREATE_USER_QUERY, {
        onCompleted: (data2) => {
            props.refetchData()
            toast(t("settings.user.toast_save_ok", { name: data2.createUser?.username }))
        },
        onError: (error2) => errorToast(error2)
    })
    const [updateUser] = useMutation(UPDATE_USER_QUERY, {
        onCompleted: (data2) => {
            props.refetchData()
            toast(t("settings.user.toast_save_ok", { name: data2.UpdateUser?.username }))
        },
        onError: (error2) => errorToast(error2)
    })

    function errorToast(error2) {
        if (error2.message.toString().length > 0) {
            toast.error(t("settings.user.toast_error", { msg: error2.message.toString(), autoClose: 10000 }))
        }
    }

    const submitCreateUser = () => {
        let userUpdateData = {
            variables: {
                user: {
                    hidden: document.getElementById("form-check-input-hidden").checked,
                    ruleEngineAccess: document.getElementById("form-check-input-reaccess").checked,
                    rights: rightsRef.value,
                    username: usernameRef.current.value,
                    password: passwordRef.current.value,
                    name: nameRef.current.value,
                    email: emailRef.current.value,
                    configLanguage: languageRef.value
                }
            }
        }
        createUser(userUpdateData)
    }

    const submitUpdateUser = (row) => {
        let fields = Array.from(
            document.querySelectorAll("div#row-" + row.id + " select, div#row-" + row.id + " textarea, div#row-" + row.id + " input")
        )
        let updateData = {}

        fields.forEach(function (obj) {
            const fieldName = DOM_TO_API[obj.dataset.ppField] || obj.name
            if (fieldName === "password" && obj.value === "") {
                return
            }
            if (obj.type === "checkbox") {
                return
            }
            updateData[fieldName] = obj.value
        })

        updateData["id"] = row.id
        updateData["hidden"] = row.hidden
        updateData["customerId"] = row.customerId
        updateData["avatarUrl"] = row.avatarUrl
        updateData["ruleEngineAccess"] = row.ruleEngineAccess

        // Customer & Shops
        if (customer_activ) {
            let fields_shops = Array.from(
                document.querySelectorAll(
                    "div.custumerbox-" + row.id + " ul#useradministrationshopslist-" + row.id + " input"
                )
            )
            let configShopsActivated = []
            fields_shops.forEach(function (obj) {
                if (obj.type === "checkbox" && obj.checked) {
                    configShopsActivated.push(obj.value)
                }
            })
            updateData["configShopsActivated"] = configShopsActivated.join(",")
            const custumerBox = document.querySelector("div.custumerbox-" + row.id)
            updateData["customerId"] = custumerBox?.dataset.customerId || row.customerId
        }

        let userUpdateData = {
            variables: {
                user: updateData
            }
        }

        updateUser(userUpdateData)
    }

    function createButtonDisable() {
        if (
            usernameRef?.current?.value?.length > 0 &&
            passwordRef?.current?.value?.length > 0 &&
            emailRef?.current?.value?.length > 0 &&
            nameRef?.current?.value?.length > 0
        ) {
            setButtonCreateDisabled(false)
        } else {
            setButtonCreateDisabled(true)
        }
    }

    return (
        <form
            autoComplete="off"
            data-bwignore="true"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            onSubmit={(e) => e.preventDefault()}>
            <div className="row mt-12 g-3 align-items-end">
                <div className="col-3">
                    <label>{t("single.rights")}</label>
                    <PPDefaultSelector
                        id="selectAdminUserSettingsRights"
                        data={rights}
                        isClearable={false}
                        onChange={(evt) => {
                            setRights(evt)
                        }}
                        defaultValue={rightsRef}
                    />
                </div>
                <div className="col-3">
                    <div>{t("single.username")}</div>
                    <AdminTextField
                        id="pp-c-f1"
                        field="f1"
                        inputRef={usernameRef}
                        onChange={createButtonDisable}
                    />
                </div>
                <div className="col-3">
                    <div>{t("single.password")}</div>
                    <AdminTextField
                        id="pp-c-f2"
                        field="f2"
                        inputRef={passwordRef}
                        onChange={createButtonDisable}
                    />
                </div>
                <div className="col-1">
                    <label>{t("single.hidden")}</label>
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            id={"form-check-input-hidden"}
                            className="form-check-input"
                            name="hidden"
                            defaultChecked={0}
                        />
                        <label className="form-check-label" htmlFor={"form-check-input-hidden"}>
                            <span className="visually-hidden">{t("single.hidden")}</span>
                        </label>
                    </div>
                </div>
                <div className="col-1">
                    <label>{t("single.ruleEngineAccess")}</label>
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            id={"form-check-input-reaccess"}
                            className="form-check-input"
                            name="reaccess"
                            defaultChecked={0}
                        />
                        <label className="form-check-label" htmlFor={"form-check-input-reaccess"}>
                            <span className="visually-hidden">{t("single.ruleEngineAccess")}</span>
                        </label>
                    </div>
                </div>
            </div>
            <div className="row mt-3 g-3 align-items-end">
                <div className="col-3">
                    <div>{t("single.name")}</div>
                    <AdminTextField
                        id="pp-c-f3"
                        field="f3"
                        inputRef={nameRef}
                        onChange={createButtonDisable}
                    />
                </div>
                <div className="col-3">
                    <div>E-Mail</div>
                    <AdminTextField
                        id="pp-c-f4"
                        field="f4"
                        inputRef={emailRef}
                        onChange={createButtonDisable}
                    />
                </div>
                <div className="col-3">
                    <label>{t("single.language")}</label>
                    <PPDefaultSelector
                        id="selectAdminUserSettingsLanguage"
                        data={languages}
                        isClearable={false}
                        onChange={(evt) => {
                            setLanguage(evt)
                        }}
                        defaultValue={languageRef}
                    />
                </div>
                <div className="col-3">
                    <label className="w-100" htmlFor="buttonAdminUserSettingsCreateUser">
                        <span className="visually-hidden">{t("single.create")}</span>
                        &nbsp;
                    </label>
                    <button
                        id="buttonAdminUserSettingsCreateUser"
                        className="category-button-go btn btn-primary w-100"
                        type="button"
                        onClick={submitCreateUser}
                        disabled={buttonCreateDisabled}>
                        {t("single.create")}
                    </button>
                </div>
            </div>
            <div className="mt-3 datatable_headerbold datatable_specialpadding pp-admin-users">
                {customer_activ ? (
                    <DataTable
                        noHeader
                        columns={columns}
                        data={props.users}
                        expandableRows={true}
                        // expandableRowExpanded={(evt) => {
                        //     return true
                        // }}
                        expandableRowsComponent={PPExpandedRowUserAdministration}
                        expandableRowsComponentProps={{ shops: props?.shops, customers: customers }}
                    />
                ) : (
                    <DataTable noHeader columns={columns} data={props.users} />
                )}
            </div>
        </form>
    )
}

//
